/**
 * Hook consolidado para sincronização realtime de comercial e logística
 * Consolida: useArteJobsSync + useLogisticsSync (6 tabelas total)
 * Otimização v2.1: 2 hooks → 1 channel (economia de 50% em WebSocket connections)
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtime } from '@/lib/realtime';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import {
  debouncedInvalidateCommerceQueries,
  debouncedInvalidateLogisticsQueries,
} from '@/utils/queryInvalidation';
import { parseDateLocal } from '@/utils/timezone';

export const useCommerceLogisticsSync = () => {
  const queryClient = useQueryClient();
  const { playSound } = useNotificationSound();
  const playSoundRef = useRef(playSound);
  
  // Map para controle de firstRender por tabela
  const firstRenderMap = useRef(new Map([
    ['arte_jobs', true],
    ['logistics_orders', true],
    ['logistics_quotes', true],
    ['delivery_proofs', true],
    ['logistics_events', true],
    ['standalone_purchases', true]
  ]));

  const shouldSkipSound = (table: string): boolean => {
    if (firstRenderMap.current.get(table)) {
      firstRenderMap.current.set(table, false);
      return true;
    }
    return false;
  };

  // Manter ref atualizado sem causar re-render
  useEffect(() => {
    playSoundRef.current = playSound;
  }, [playSound]);

  useEffect(() => {
    // Walter 2026-05-17: removido guard `isSystemActive()` (ver useProductionCoreSync).
    console.log('🔔 Commerce Logistics Sync: Inicializando channel consolidado (7 listeners)');

    const resetTimeout = setTimeout(() => {
      firstRenderMap.current.forEach((_, key) => {
        firstRenderMap.current.set(key, false);
      });
    }, 2000);

    const channel = realtime
      .channel('commerce-logistics-sync')
      
      // Listener 1: Arte Jobs
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'arte_jobs' },
        (payload) => {
          console.log('🔔 Commerce: arte_jobs mudou', payload.eventType);
          debouncedInvalidateCommerceQueries(queryClient, { immediate: true });
          queryClient.invalidateQueries({ queryKey: ['painel'] });
          // HOTFIX 3 (Walter 2026-05-18): refetchQueries forçado removido (d638362e).
          // Combinado com sleep mode removido (b45f288) causou storm CPU 103% em
          // segunda 18/Mai. Cada UPDATE em arte_jobs × N abas × refetch 750KB.
          
          if (shouldSkipSound('arte_jobs')) return;
          
          // Som para jobs urgentes
          if (payload.eventType === 'INSERT') {
            const newJob = payload.new as { prioridade?: string; data_entrega?: string };
            const isUrgent = newJob.prioridade === 'URGENTE';
            // data_entrega é text "YYYY-MM-DD" — parseDateLocal evita bug D-1.
            // Janela [-24h, 48h): "hoje" ainda alerta; data antiga (re-sync de
            // pedido já entregue) não dispara o som.
            const delta = newJob.data_entrega
              ? parseDateLocal(newJob.data_entrega).getTime() - Date.now()
              : null;
            const is48h = delta !== null && delta > -24 * 60 * 60 * 1000 && delta < 48 * 60 * 60 * 1000;
            
            if (isUrgent || is48h) {
              playSoundRef.current('logistics_alert');
            }
          }
          
          // Som para aprovação
          if (payload.eventType === 'UPDATE') {
            const oldJob = payload.old as { status?: string };
            const newJob = payload.new as { status?: string };
            if (oldJob.status !== 'APROVADO' && newJob.status === 'APROVADO') {
              playSoundRef.current('ready_pickup');
            }
            // Som para FALTA_ALGO
            if (oldJob.status !== 'FALTA_ALGO' && newJob.status === 'FALTA_ALGO') {
              playSoundRef.current('logistics_alert');
            }
          }
        }
      )
      
      // Listener 2: Logistics Orders
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'logistics_orders' },
        (payload) => {
          console.log('🔔 Commerce: logistics_orders mudou', payload.eventType);
          debouncedInvalidateLogisticsQueries(queryClient, { immediate: true });
          queryClient.invalidateQueries({ queryKey: ['arte-job-delivery'] });
          queryClient.invalidateQueries({ queryKey: ['painel'] });   // card/drawer de LOGISTICA leem o resumo, que não tem polling
          
          if (shouldSkipSound('logistics_orders')) return;
          
          if (payload.eventType === 'UPDATE') {
            const oldOrder = payload.old as { status?: string };
            const newOrder = payload.new as { status?: string; integration_status?: string };
            
            // Som para pronto para coleta
            if (oldOrder.status !== 'AGUARDANDO_COLETA' && newOrder.status === 'AGUARDANDO_COLETA') {
              playSoundRef.current('ready_pickup');
            }
            
            // Som para erro
            if (newOrder.integration_status?.includes('ERRO') || 
                newOrder.integration_status?.includes('FALHA')) {
              playSoundRef.current('logistics_alert');
            }
          }
        }
      )
      
      // Listener 3: Logistics Quotes
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'logistics_quotes' },
        (payload) => {
          console.log('🔔 Commerce: logistics_quotes mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['logistics-quotes'], refetchType: 'active' });
          debouncedInvalidateLogisticsQueries(queryClient, { immediate: true });
        }
      )
      
      // Listener 4: Delivery Proofs
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'delivery_proofs' },
        (payload) => {
          console.log('🔔 Commerce: delivery_proofs mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['delivery-proofs'], refetchType: 'active' });
          debouncedInvalidateLogisticsQueries(queryClient, { immediate: true });
        }
      )
      
      // Listener 5: Logistics Events
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'logistics_events' },
        (payload) => {
          console.log('🔔 Commerce: logistics_events mudou', payload.eventType);
          const record = (payload.new || payload.old) as { logistics_order_id?: string };
          if (record?.logistics_order_id) {
            queryClient.invalidateQueries({ 
              queryKey: ['logistics-events', record.logistics_order_id],
              refetchType: 'active'
            });
          }
        }
      )
      
      // Listener 6: Standalone Purchases
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'standalone_purchases' },
        (payload) => {
          console.log('🔔 Commerce: standalone_purchases mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['standalone-purchases'] });
        }
      )

      // Listener 7: Orders (cross-table invalidation)
      // useLogisticsOrders faz JOIN manual com orders (financeiro_*, link_layout,
      // gestaoclick_*) — quando esses campos mudam (ex: automation-engine
      // sincroniza label "Entrada OK"/"Pagto Final OK"/"NF Emitida" para
      // orders.financeiro_*), o cache ['logistics-orders'] precisa ser
      // invalidado para o card de Logistica refletir as badges/botoes.
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          console.log('🔔 Commerce: orders mudou (UPDATE)', payload.eventType);
          debouncedInvalidateLogisticsQueries(queryClient, { immediate: true });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Commerce Logistics Sync: 7 listeners ativos em 1 channel');
        }
      });

    return () => {
      console.log('🔕 Commerce Logistics Sync: Removendo channel');
      clearTimeout(resetTimeout);
      realtime.removeChannel(channel);
    };
  }, [queryClient]);
};
